/**
 * Airport Database for Client-Side Search
 * Static list for offline autocomplete (no backend).
 * Core curated rows + Europe + Africa + Australia + Asia + USA + Canada IATA sets from OurAirports (see *AirportsData.js).
 * AT/BE/FR/DE/LI/LU/MC/NL/CH: `europeAirportsData.js` + scripts/audit-cwe-vs-europe.mjs.
 * AL/AD/BA/HR/GR/IT/MT/ME/MK/PT/SM/RS/SI/ES/VA: `southEuropeAirportsData.js` (iso_country; adds ES/GR outside continent EU, e.g. Canary Islands).
 * BY/BG/CZ/HU/MD/PL/RO/RU/SK/UA: `eastEuropeAirportsData.js` (iso_country; RU includes Asian airports, same ISO).
 * TR + KZ: covered by `europeAirportsData.js` + `asiaAirportsData.js` (continent splits). RU: east file. Check: scripts/audit-tr-kz-ru.mjs
 * Africa: `africaAirportsData.js` by iso_country (54 states + EH). Verify: scripts/audit-africa-iso-gaps.mjs
 * Asia: `asiaAirportsData.js` by iso_country (user list). Americas (non US/CA): `americasLatamAirportsData.js`. Oceania (non AU): `oceaniaExtraAirportsData.js`.
 */

import { africaAirports } from './africaAirportsData.js';
import { americasLatamAirports } from './americasLatamAirportsData.js';
import { asiaAirports } from './asiaAirportsData.js';
import { australiaAirports } from './australiaAirportsData.js';
import { canadaAirports } from './canadaAirportsData.js';
import { eastEuropeAirports } from './eastEuropeAirportsData.js';
import { europeAirports } from './europeAirportsData.js';
import { oceaniaExtraAirports } from './oceaniaExtraAirportsData.js';
import { southEuropeAirports } from './southEuropeAirportsData.js';
import { usaAirports } from './usaAirportsData.js';

function mergeAirportsByIata(core, extra) {
  const seen = new Set(core.map((a) => a.iataCode));
  const out = [...core];
  for (const a of extra) {
    if (seen.has(a.iataCode)) continue;
    seen.add(a.iataCode);
    out.push(a);
  }
  return out;
}

const coreAirports = [
  // Nigeria
  { iataCode: 'LOS', name: 'Murtala Muhammed International Airport', city: 'Lagos', country: 'NG', type: 'AIRPORT' },
  { iataCode: 'ABV', name: 'Nnamdi Azikiwe International Airport', city: 'Abuja', country: 'NG', type: 'AIRPORT' },
  { iataCode: 'PHC', name: 'Port Harcourt International Airport', city: 'Port Harcourt', country: 'NG', type: 'AIRPORT' },
  { iataCode: 'KAN', name: 'Mallam Aminu Kano International Airport', city: 'Kano', country: 'NG', type: 'AIRPORT' },
  { iataCode: 'ENU', name: 'Akanu Ibiam International Airport', city: 'Enugu', country: 'NG', type: 'AIRPORT' },
  { iataCode: 'BNI', name: 'Benin Airport', city: 'Benin', country: 'NG', type: 'AIRPORT' },
  
  // United Kingdom (GB) — IATA codes; Crown Dependencies & Channel Islands included
  // London & South East England
  { iataCode: 'LHR', name: 'London Heathrow Airport', city: 'London', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'LGW', name: 'London Gatwick Airport', city: 'London', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'STN', name: 'London Stansted Airport', city: 'London', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'LTN', name: 'London Luton Airport', city: 'London', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'SEN', name: 'London Southend Airport', city: 'London', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'LCY', name: 'London City Airport', city: 'London', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'LYX', name: 'London Ashford Airport (Lydd)', city: 'Lydd', country: 'GB', type: 'AIRPORT' },
  // England — Midlands & North
  { iataCode: 'BHX', name: 'Birmingham Airport', city: 'Birmingham', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'EMA', name: 'East Midlands Airport', city: 'East Midlands', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'NQT', name: 'Nottingham Airport', city: 'Nottingham', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'HUY', name: 'Humberside Airport', city: 'Grimsby', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'LBA', name: 'Leeds Bradford Airport', city: 'Leeds', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'LPL', name: 'Liverpool John Lennon Airport', city: 'Liverpool', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'MAN', name: 'Manchester Airport', city: 'Manchester', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'MME', name: 'Teesside International Airport', city: 'Darlington', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'NCL', name: 'Newcastle International Airport', city: 'Newcastle upon Tyne', country: 'GB', type: 'AIRPORT' },
  // England — South & South West
  { iataCode: 'BOH', name: 'Bournemouth Airport', city: 'Bournemouth', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'BRS', name: 'Bristol Airport', city: 'Bristol', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'EXT', name: 'Exeter Airport', city: 'Exeter', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'NQY', name: 'Cornwall Airport Newquay', city: 'Newquay', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'SOU', name: 'Southampton Airport', city: 'Southampton', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'ISC', name: 'St Mary\'s Airport', city: 'Isles of Scilly', country: 'GB', type: 'AIRPORT' },
  // England — East
  { iataCode: 'NWI', name: 'Norwich Airport', city: 'Norwich', country: 'GB', type: 'AIRPORT' },
  // England — other
  { iataCode: 'CAX', name: 'Carlisle Lake District Airport', city: 'Carlisle', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'CEG', name: 'Hawarden Airport', city: 'Chester', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'OXF', name: 'London Oxford Airport', city: 'Oxford', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'BLK', name: 'Blackpool Airport', city: 'Blackpool', country: 'GB', type: 'AIRPORT' },
  // Wales
  { iataCode: 'CWL', name: 'Cardiff Airport', city: 'Cardiff', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'SWS', name: 'Swansea Airport', city: 'Swansea', country: 'GB', type: 'AIRPORT' },
  // Scotland — mainland & islands
  { iataCode: 'ABZ', name: 'Aberdeen Airport', city: 'Aberdeen', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'BRR', name: 'Barra Airport', city: 'Barra', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'BEB', name: 'Benbecula Airport', city: 'Benbecula', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'CAL', name: 'Campbeltown Airport', city: 'Campbeltown', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'COL', name: 'Coll Airport', city: 'Coll', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'DND', name: 'Dundee Airport', city: 'Dundee', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'EDI', name: 'Edinburgh Airport', city: 'Edinburgh', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'FIE', name: 'Fair Isle Airport', city: 'Fair Isle', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'GLA', name: 'Glasgow Airport', city: 'Glasgow', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'PIK', name: 'Glasgow Prestwick Airport', city: 'Prestwick', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'ILY', name: 'Islay Airport', city: 'Islay', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'INV', name: 'Inverness Airport', city: 'Inverness', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'KOI', name: 'Kirkwall Airport', city: 'Kirkwall', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'LSI', name: 'Sumburgh Airport', city: 'Shetland', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'NRL', name: 'North Ronaldsay Airport', city: 'North Ronaldsay', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'OBN', name: 'Oban Airport', city: 'Oban', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'PPW', name: 'Papa Westray Airport', city: 'Papa Westray', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'SKL', name: 'Skye Airport (Broadford)', city: 'Isle of Skye', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'SYY', name: 'Stornoway Airport', city: 'Stornoway', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'TRE', name: 'Tiree Airport', city: 'Tiree', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'WRY', name: 'Westray Airport', city: 'Westray', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'WIC', name: 'Wick John O\'Groats Airport', city: 'Wick', country: 'GB', type: 'AIRPORT' },
  // Northern Ireland
  { iataCode: 'BFS', name: 'Belfast International Airport', city: 'Belfast', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'BHD', name: 'George Best Belfast City Airport', city: 'Belfast', country: 'GB', type: 'AIRPORT' },
  { iataCode: 'LDY', name: 'City of Derry Airport', city: 'Derry', country: 'GB', type: 'AIRPORT' },
  // Crown Dependencies & Channel Islands (ISO country codes: IM, JE, GG)
  { iataCode: 'IOM', name: 'Isle of Man Airport', city: 'Isle of Man', country: 'IM', type: 'AIRPORT' },
  { iataCode: 'JER', name: 'Jersey Airport', city: 'Jersey', country: 'JE', type: 'AIRPORT' },
  { iataCode: 'GCI', name: 'Guernsey Airport', city: 'Guernsey', country: 'GG', type: 'AIRPORT' },
  { iataCode: 'ACI', name: 'Alderney Airport', city: 'Alderney', country: 'GG', type: 'AIRPORT' },
  
  // United Arab Emirates
  { iataCode: 'DXB', name: 'Dubai International Airport', city: 'Dubai', country: 'AE', type: 'AIRPORT' },
  { iataCode: 'AUH', name: 'Abu Dhabi International Airport', city: 'Abu Dhabi', country: 'AE', type: 'AIRPORT' },
  { iataCode: 'SHJ', name: 'Sharjah International Airport', city: 'Sharjah', country: 'AE', type: 'AIRPORT' },
  
  // United States
  { iataCode: 'JFK', name: 'John F. Kennedy International Airport', city: 'New York', country: 'US', type: 'AIRPORT' },
  { iataCode: 'LAX', name: 'Los Angeles International Airport', city: 'Los Angeles', country: 'US', type: 'AIRPORT' },
  { iataCode: 'ORD', name: 'Chicago O\'Hare International Airport', city: 'Chicago', country: 'US', type: 'AIRPORT' },
  { iataCode: 'MIA', name: 'Miami International Airport', city: 'Miami', country: 'US', type: 'AIRPORT' },
  { iataCode: 'ATL', name: 'Hartsfield-Jackson Atlanta International Airport', city: 'Atlanta', country: 'US', type: 'AIRPORT' },
  
  // Ghana
  { iataCode: 'ACC', name: 'Kotoka International Airport', city: 'Accra', country: 'GH', type: 'AIRPORT' },
  
  // South Africa
  { iataCode: 'JNB', name: 'O.R. Tambo International Airport', city: 'Johannesburg', country: 'ZA', type: 'AIRPORT' },
  { iataCode: 'CPT', name: 'Cape Town International Airport', city: 'Cape Town', country: 'ZA', type: 'AIRPORT' },
  
  // France
  { iataCode: 'CDG', name: 'Charles de Gaulle Airport', city: 'Paris', country: 'FR', type: 'AIRPORT' },
  { iataCode: 'ORY', name: 'Orly Airport', city: 'Paris', country: 'FR', type: 'AIRPORT' },
  
  // Turkey
  { iataCode: 'IST', name: 'Istanbul Airport', city: 'Istanbul', country: 'TR', type: 'AIRPORT' },
  { iataCode: 'SAW', name: 'Sabiha Gökçen International Airport', city: 'Istanbul', country: 'TR', type: 'AIRPORT' },
  
  // Germany
  { iataCode: 'FRA', name: 'Frankfurt Airport', city: 'Frankfurt', country: 'DE', type: 'AIRPORT' },
  { iataCode: 'MUC', name: 'Munich Airport', city: 'Munich', country: 'DE', type: 'AIRPORT' },
  
  // Netherlands
  { iataCode: 'AMS', name: 'Amsterdam Airport Schiphol', city: 'Amsterdam', country: 'NL', type: 'AIRPORT' },
  
  // Spain
  { iataCode: 'MAD', name: 'Adolfo Suárez Madrid-Barajas Airport', city: 'Madrid', country: 'ES', type: 'AIRPORT' },
  { iataCode: 'BCN', name: 'Barcelona-El Prat Airport', city: 'Barcelona', country: 'ES', type: 'AIRPORT' },
  
  // Italy
  { iataCode: 'FCO', name: 'Leonardo da Vinci-Fiumicino Airport', city: 'Rome', country: 'IT', type: 'AIRPORT' },
  { iataCode: 'MXP', name: 'Milan Malpensa Airport', city: 'Milan', country: 'IT', type: 'AIRPORT' },
  
  // Canada
  { iataCode: 'YYZ', name: 'Toronto Pearson International Airport', city: 'Toronto', country: 'CA', type: 'AIRPORT' },
  { iataCode: 'YVR', name: 'Vancouver International Airport', city: 'Vancouver', country: 'CA', type: 'AIRPORT' },
  
  // Australia
  { iataCode: 'SYD', name: 'Sydney Kingsford Smith Airport', city: 'Sydney', country: 'AU', type: 'AIRPORT' },
  { iataCode: 'MEL', name: 'Melbourne Airport', city: 'Melbourne', country: 'AU', type: 'AIRPORT' },
  
  // Asia
  { iataCode: 'DOH', name: 'Hamad International Airport', city: 'Doha', country: 'QA', type: 'AIRPORT' },
  { iataCode: 'BKK', name: 'Suvarnabhumi Airport', city: 'Bangkok', country: 'TH', type: 'AIRPORT' },
  { iataCode: 'SIN', name: 'Singapore Changi Airport', city: 'Singapore', country: 'SG', type: 'AIRPORT' },
  { iataCode: 'HKG', name: 'Hong Kong International Airport', city: 'Hong Kong', country: 'HK', type: 'AIRPORT' },
  { iataCode: 'NRT', name: 'Narita International Airport', city: 'Tokyo', country: 'JP', type: 'AIRPORT' },
  { iataCode: 'HND', name: 'Haneda Airport', city: 'Tokyo', country: 'JP', type: 'AIRPORT' },
  { iataCode: 'PEK', name: 'Beijing Capital International Airport', city: 'Beijing', country: 'CN', type: 'AIRPORT' },
  { iataCode: 'PVG', name: 'Shanghai Pudong International Airport', city: 'Shanghai', country: 'CN', type: 'AIRPORT' },
  { iataCode: 'DEL', name: 'Indira Gandhi International Airport', city: 'New Delhi', country: 'IN', type: 'AIRPORT' },
  { iataCode: 'BOM', name: 'Chhatrapati Shivaji Maharaj International Airport', city: 'Mumbai', country: 'IN', type: 'AIRPORT' },
];

/** Full merged list (core + regional datasets; core wins on duplicate IATA) */
const _worldToOceania = mergeAirportsByIata(
  mergeAirportsByIata(
    mergeAirportsByIata(
      mergeAirportsByIata(mergeAirportsByIata(coreAirports, europeAirports), southEuropeAirports),
      eastEuropeAirports
    ),
    africaAirports
  ),
  australiaAirports
);
const _asiaAmericasPacific = mergeAirportsByIata(
  mergeAirportsByIata(asiaAirports, americasLatamAirports),
  oceaniaExtraAirports
);
const _throughUs = mergeAirportsByIata(mergeAirportsByIata(_worldToOceania, _asiaAmericasPacific), usaAirports);
export const airportDatabase = mergeAirportsByIata(_throughUs, canadaAirports);

/** OurAirports continent EU + explicit Central/West ISO codes (incl. microstates with no IATA row) */
const CWE_COUNTRY_CODES = new Set(['AT', 'BE', 'FR', 'DE', 'LI', 'LU', 'MC', 'NL', 'CH']);
/** Southern/Mediterranean Europe (iso_country list; matches southEuropeAirportsData.js) */
const SEU_COUNTRY_CODES = new Set([
  'AL', 'AD', 'BA', 'HR', 'GR', 'IT', 'MT', 'ME', 'MK', 'PT', 'SM', 'RS', 'SI', 'ES', 'VA',
]);
/** Central/Eastern Europe + Russia + Ukraine (matches eastEuropeAirportsData.js) */
const CEE_COUNTRY_CODES = new Set(['BY', 'BG', 'CZ', 'HU', 'MD', 'PL', 'RO', 'RU', 'SK', 'UA']);
const EUROPE_CODES = new Set([
  ...europeAirports.map((a) => a.country),
  ...CWE_COUNTRY_CODES,
  ...SEU_COUNTRY_CODES,
  ...CEE_COUNTRY_CODES,
]);

/** Type country name (or 2-letter ISO) to list airports — matches `airportDatabase` rows for that country */
const CWE_NAME_TO_ISO = new Map([
  ['austria', 'AT'],
  ['österreich', 'AT'],
  ['belgium', 'BE'],
  ['france', 'FR'],
  ['germany', 'DE'],
  ['deutschland', 'DE'],
  ['liechtenstein', 'LI'],
  ['luxembourg', 'LU'],
  ['monaco', 'MC'],
  ['netherlands', 'NL'],
  ['holland', 'NL'],
  ['switzerland', 'CH'],
  ['schweiz', 'CH'],
  ['suisse', 'CH'],
]);

const SEU_NAME_TO_ISO = new Map([
  ['albania', 'AL'],
  ['andorra', 'AD'],
  ['bosnia', 'BA'],
  ['bosnia and herzegovina', 'BA'],
  ['croatia', 'HR'],
  ['greece', 'GR'],
  ['hellas', 'GR'],
  ['italy', 'IT'],
  ['italia', 'IT'],
  ['malta', 'MT'],
  ['montenegro', 'ME'],
  ['north macedonia', 'MK'],
  ['macedonia', 'MK'],
  ['portugal', 'PT'],
  ['san marino', 'SM'],
  ['serbia', 'RS'],
  ['slovenia', 'SI'],
  ['spain', 'ES'],
  ['españa', 'ES'],
  ['espana', 'ES'],
  ['vatican', 'VA'],
  ['vatican city', 'VA'],
]);

const CEE_NAME_TO_ISO = new Map([
  ['belarus', 'BY'],
  ['bulgaria', 'BG'],
  ['czech republic', 'CZ'],
  ['czechia', 'CZ'],
  ['hungary', 'HU'],
  ['moldova', 'MD'],
  ['poland', 'PL'],
  ['romania', 'RO'],
  ['russia', 'RU'],
  ['slovakia', 'SK'],
  ['ukraine', 'UA'],
]);

/** Turkey / Kazakhstan / Russia — all IATA already merged; `russia` is in CEE_NAME_TO_ISO */
const TKR_COUNTRY_CODES = new Set(['TR', 'KZ', 'RU']);
const TKR_EXTRA_NAME_TO_ISO = new Map([
  ['turkey', 'TR'],
  ['türkiye', 'TR'],
  ['turkiye', 'TR'],
  ['kazakhstan', 'KZ'],
]);

/** ISO country codes in the Africa dataset (sovereign Africa + EH; see build-africa-airports.mjs) */
const AFRICA_CODES = new Set(africaAirports.map((a) => a.country));

const NORTH_AFRICA_CODES = new Set(['DZ', 'EG', 'LY', 'MA', 'SD', 'TN', 'EH']);
const WEST_AFRICA_CODES = new Set([
  'BJ', 'BF', 'CV', 'CI', 'GM', 'GH', 'GN', 'GW', 'LR', 'ML', 'MR', 'NE', 'NG', 'SN', 'SL', 'TG',
]);
const CENTRAL_AFRICA_CODES = new Set(['AO', 'CM', 'CF', 'TD', 'CD', 'CG', 'GQ', 'GA', 'ST']);
const EAST_AFRICA_CODES = new Set([
  'BI', 'KM', 'DJ', 'ER', 'ET', 'KE', 'MG', 'MW', 'MU', 'MZ', 'RW', 'SC', 'SO', 'SS', 'TZ', 'UG', 'ZM', 'ZW',
]);
const SOUTHERN_AFRICA_REGION_CODES = new Set(['BW', 'SZ', 'LS', 'NA', 'ZA']);

const AFRICA_REGION_PHRASE_TO_CODES = new Map([
  ['north africa', NORTH_AFRICA_CODES],
  ['northern africa', NORTH_AFRICA_CODES],
  ['west africa', WEST_AFRICA_CODES],
  ['western africa', WEST_AFRICA_CODES],
  ['central africa', CENTRAL_AFRICA_CODES],
  ['east africa', EAST_AFRICA_CODES],
  ['eastern africa', EAST_AFRICA_CODES],
  ['southern africa', SOUTHERN_AFRICA_REGION_CODES],
]);

const AFRICA_NAME_TO_ISO = new Map([
  ['algeria', 'DZ'],
  ['egypt', 'EG'],
  ['libya', 'LY'],
  ['morocco', 'MA'],
  ['sudan', 'SD'],
  ['tunisia', 'TN'],
  ['western sahara', 'EH'],
  ['benin', 'BJ'],
  ['burkina faso', 'BF'],
  ['cabo verde', 'CV'],
  ['cape verde', 'CV'],
  ['ivory coast', 'CI'],
  ['cote divoire', 'CI'],
  ['gambia', 'GM'],
  ['ghana', 'GH'],
  ['guinea', 'GN'],
  ['guinea-bissau', 'GW'],
  ['liberia', 'LR'],
  ['mali', 'ML'],
  ['mauritania', 'MR'],
  ['niger', 'NE'],
  ['nigeria', 'NG'],
  ['senegal', 'SN'],
  ['sierra leone', 'SL'],
  ['togo', 'TG'],
  ['angola', 'AO'],
  ['cameroon', 'CM'],
  ['central african republic', 'CF'],
  ['chad', 'TD'],
  ['democratic republic of the congo', 'CD'],
  ['dr congo', 'CD'],
  ['drc', 'CD'],
  ['republic of the congo', 'CG'],
  ['congo brazzaville', 'CG'],
  ['equatorial guinea', 'GQ'],
  ['gabon', 'GA'],
  ['são tomé and príncipe', 'ST'],
  ['sao tome and principe', 'ST'],
  ['burundi', 'BI'],
  ['comoros', 'KM'],
  ['djibouti', 'DJ'],
  ['eritrea', 'ER'],
  ['ethiopia', 'ET'],
  ['kenya', 'KE'],
  ['madagascar', 'MG'],
  ['malawi', 'MW'],
  ['mauritius', 'MU'],
  ['mozambique', 'MZ'],
  ['rwanda', 'RW'],
  ['seychelles', 'SC'],
  ['somalia', 'SO'],
  ['south sudan', 'SS'],
  ['tanzania', 'TZ'],
  ['uganda', 'UG'],
  ['zambia', 'ZM'],
  ['zimbabwe', 'ZW'],
  ['botswana', 'BW'],
  ['eswatini', 'SZ'],
  ['swaziland', 'SZ'],
  ['lesotho', 'LS'],
  ['namibia', 'NA'],
  ['south africa', 'ZA'],
]);

/** Australia (ISO AU) — all rows in australiaAirports are AU */
const AUSTRALIA_CODES = new Set(australiaAirports.map((a) => a.country));

/** ISO country codes in the Asia dataset (iso list; see build-asia-airports.mjs) */
const ASIA_CODES = new Set(asiaAirports.map((a) => a.country));

/** United States (OurAirports iso_country US) */
const USA_CODES = new Set(usaAirports.map((a) => a.country));

/** Canada (OurAirports iso_country CA) */
const CANADA_CODES = new Set(canadaAirports.map((a) => a.country));

/** Mexico, Central America, Caribbean, South America (americasLatamAirportsData.js) */
const AMERICAS_LATAM_CODES = new Set(americasLatamAirports.map((a) => a.country));
/** NZ + Pacific — AU is in australiaAirports only */
const OCEANIA_EXTRA_CODES = new Set(oceaniaExtraAirports.map((a) => a.country));
const OCEANIA_ALL_CODES = new Set([...AUSTRALIA_CODES, ...OCEANIA_EXTRA_CODES]);

const NORTH_AMERICA_THREE_CODES = new Set(['CA', 'US', 'MX']);
const CENTRAL_AMERICA_ONLY_CODES = new Set(['BZ', 'CR', 'SV', 'GT', 'HN', 'NI', 'PA']);
const CARIBBEAN_ONLY_CODES = new Set(['AG', 'BS', 'BB', 'CU', 'DM', 'DO', 'GD', 'HT', 'JM', 'KN', 'LC', 'VC', 'TT']);
const SOUTH_AMERICA_ONLY_CODES = new Set(['AR', 'BO', 'BR', 'CL', 'CO', 'EC', 'GY', 'PY', 'PE', 'SR', 'UY', 'VE']);

const AMERICAS_SUBREGION_PHRASE = new Map([
  ['north america', NORTH_AMERICA_THREE_CODES],
  ['northern america', NORTH_AMERICA_THREE_CODES],
  ['central america', CENTRAL_AMERICA_ONLY_CODES],
  ['caribbean', CARIBBEAN_ONLY_CODES],
  ['south america', SOUTH_AMERICA_ONLY_CODES],
  ['latin america', AMERICAS_LATAM_CODES],
  [
    'americas',
    new Set([...CANADA_CODES, ...USA_CODES, ...AMERICAS_LATAM_CODES]),
  ],
]);

/** GB + Crown Dependencies used for UK travel search */
const UK_COUNTRY_CODES = new Set(['GB', 'IM', 'JE', 'GG']);

/** Scottish airports (IATA) — keyword "scotland" / "scottish" */
const SCOTLAND_IATA = new Set([
  'ABZ', 'BRR', 'BEB', 'CAL', 'COL', 'DND', 'EDI', 'FIE', 'GLA', 'PIK', 'ILY', 'INV', 'KOI', 'LSI',
  'NRL', 'OBN', 'PPW', 'SKL', 'SYY', 'TRE', 'WRY', 'WIC',
]);

const WALES_IATA = new Set(['CWL', 'SWS']);
const NORTHERN_IRELAND_IATA = new Set(['BFS', 'BHD', 'LDY']);

function formatAirportForUi(airport) {
  return {
    type: airport.type === 'AIRPORT' ? 'airport' : 'city',
    subType: airport.type,
    name: airport.name,
    iataCode: airport.iataCode,
    address: {
      cityName: airport.city,
      countryCode: airport.country,
    },
    geoCode: null,
  };
}

function airportMatchesQuery(airport, searchQuery) {
  const n = airport.name.toLowerCase();
  const c = airport.city.toLowerCase();
  const code = airport.iataCode.toLowerCase();
  return n.includes(searchQuery) || c.includes(searchQuery) || code.includes(searchQuery);
}

/**
 * Search airports by query (name, city, or IATA code).
 * Match order: UK → Africa → Europe → Australia → Asia → LatAm/Carib → Oceania(excl. AU in bucket) → USA → Canada → rest (caps at 65).
 * Plus: `oceania`, `north america`, `latin america`, `caribbean`, … / CWE / SEU / CEE / TKR / Africa region maps.
 * @param {string} query - Search query (minimum 2 characters)
 * @returns {Array} Array of matching airports
 */
export const searchAirportsLocal = (query) => {
  if (!query || query.length < 2) {
    return [];
  }

  const searchQuery = query.toLowerCase().trim();

  // Whole-region shortcuts (search form: type "uk", "scotland", etc.)
  if (searchQuery === 'uk' || searchQuery === 'u.k' || searchQuery === 'u.k.' || searchQuery === 'gb') {
    return airportDatabase
      .filter((a) => UK_COUNTRY_CODES.has(a.country))
      .map(formatAirportForUi)
      .slice(0, 35);
  }
  if (searchQuery === 'united kingdom' || searchQuery === 'great britain' || searchQuery === 'britain' || searchQuery === 'british') {
    return airportDatabase
      .filter((a) => a.country === 'GB')
      .map(formatAirportForUi)
      .slice(0, 35);
  }
  if (searchQuery === 'scotland' || searchQuery === 'scottish') {
    return airportDatabase
      .filter((a) => SCOTLAND_IATA.has(a.iataCode))
      .map(formatAirportForUi);
  }
  if (searchQuery === 'wales' || searchQuery === 'welsh') {
    return airportDatabase.filter((a) => WALES_IATA.has(a.iataCode)).map(formatAirportForUi);
  }
  if (searchQuery === 'northern ireland' || searchQuery === 'northern ire') {
    return airportDatabase.filter((a) => NORTHERN_IRELAND_IATA.has(a.iataCode)).map(formatAirportForUi);
  }
  if (
    searchQuery === 'europe' ||
    searchQuery === 'european' ||
    searchQuery === 'eu' ||
    searchQuery === 'continental europe'
  ) {
    return airportDatabase
      .filter((a) => EUROPE_CODES.has(a.country))
      .map(formatAirportForUi)
      .slice(0, 100);
  }
  if (
    searchQuery === 'africa' ||
    searchQuery === 'african' ||
    searchQuery === 'af' ||
    searchQuery === 'sub-saharan' ||
    searchQuery === 'subsaharan'
  ) {
    return airportDatabase
      .filter((a) => AFRICA_CODES.has(a.country))
      .map(formatAirportForUi)
      .slice(0, 1200);
  }
  const africaRegionCodes = AFRICA_REGION_PHRASE_TO_CODES.get(searchQuery);
  if (africaRegionCodes) {
    return airportDatabase
      .filter((a) => africaRegionCodes.has(a.country))
      .map(formatAirportForUi)
      .slice(0, 600);
  }
  const africaNameIso = AFRICA_NAME_TO_ISO.get(searchQuery);
  if (africaNameIso) {
    return airportDatabase
      .filter((a) => a.country === africaNameIso)
      .map(formatAirportForUi)
      .slice(0, 400);
  }
  if (
    searchQuery === 'australia' ||
    searchQuery === 'australian' ||
    searchQuery === 'au' ||
    searchQuery === 'oz'
  ) {
    return airportDatabase
      .filter((a) => AUSTRALIA_CODES.has(a.country))
      .map(formatAirportForUi)
      .slice(0, 700);
  }
  if (searchQuery === 'oceania' || searchQuery === 'australasia' || searchQuery === 'pacific islands') {
    return airportDatabase
      .filter((a) => OCEANIA_ALL_CODES.has(a.country))
      .map(formatAirportForUi)
      .slice(0, 2000);
  }
  const americasSub = AMERICAS_SUBREGION_PHRASE.get(searchQuery);
  if (americasSub) {
    return airportDatabase
      .filter((a) => americasSub.has(a.country))
      .map(formatAirportForUi)
      .slice(0, 2000);
  }
  if (
    searchQuery === 'asia' ||
    searchQuery === 'asian' ||
    searchQuery === 'apac' ||
    searchQuery === 'asia pacific'
  ) {
    return airportDatabase
      .filter((a) => ASIA_CODES.has(a.country))
      .map(formatAirportForUi)
      .slice(0, 2500);
  }
  if (
    searchQuery === 'usa' ||
    searchQuery === 'us' ||
    searchQuery === 'u.s.' ||
    searchQuery === 'u.s.a.' ||
    searchQuery === 'united states' ||
    searchQuery === 'united states of america' ||
    searchQuery === 'america' ||
    searchQuery === 'american'
  ) {
    return airportDatabase
      .filter((a) => USA_CODES.has(a.country))
      .map(formatAirportForUi)
      .slice(0, 100);
  }
  if (
    searchQuery === 'canada' ||
    searchQuery === 'canadian' ||
    searchQuery === 'ca' ||
    searchQuery === 'cdn'
  ) {
    return airportDatabase
      .filter((a) => CANADA_CODES.has(a.country))
      .map(formatAirportForUi)
      .slice(0, 100);
  }

  if (searchQuery === 'cwe' || searchQuery === 'core western europe') {
    return airportDatabase
      .filter((a) => CWE_COUNTRY_CODES.has(a.country))
      .map(formatAirportForUi)
      .slice(0, 300);
  }
  if (searchQuery === 'seu' || searchQuery === 'southern europe' || searchQuery === 'med europe') {
    return airportDatabase
      .filter((a) => SEU_COUNTRY_CODES.has(a.country))
      .map(formatAirportForUi)
      .slice(0, 400);
  }
  if (searchQuery === 'cee' || searchQuery === 'eastern europe') {
    return airportDatabase
      .filter((a) => CEE_COUNTRY_CODES.has(a.country))
      .map(formatAirportForUi)
      .slice(0, 400);
  }
  if (searchQuery === 'tkr' || searchQuery === 'turkey kazakhstan russia') {
    return airportDatabase
      .filter((a) => TKR_COUNTRY_CODES.has(a.country))
      .map(formatAirportForUi)
      .slice(0, 400);
  }

  const cweFromName = CWE_NAME_TO_ISO.get(searchQuery);
  if (cweFromName) {
    return airportDatabase
      .filter((a) => a.country === cweFromName)
      .map(formatAirportForUi)
      .slice(0, 300);
  }
  const seuFromName = SEU_NAME_TO_ISO.get(searchQuery);
  if (seuFromName) {
    return airportDatabase
      .filter((a) => a.country === seuFromName)
      .map(formatAirportForUi)
      .slice(0, 300);
  }
  const ceeFromName = CEE_NAME_TO_ISO.get(searchQuery);
  if (ceeFromName) {
    return airportDatabase
      .filter((a) => a.country === ceeFromName)
      .map(formatAirportForUi)
      .slice(0, 400);
  }
  const tkrExtraName = TKR_EXTRA_NAME_TO_ISO.get(searchQuery);
  if (tkrExtraName) {
    return airportDatabase
      .filter((a) => a.country === tkrExtraName)
      .map(formatAirportForUi)
      .slice(0, 400);
  }
  if (searchQuery.length === 2) {
    const iso2 = searchQuery.toUpperCase();
    if (CWE_COUNTRY_CODES.has(iso2)) {
      return airportDatabase
        .filter((a) => a.country === iso2)
        .map(formatAirportForUi)
        .slice(0, 300);
    }
    if (SEU_COUNTRY_CODES.has(iso2)) {
      return airportDatabase
        .filter((a) => a.country === iso2)
        .map(formatAirportForUi)
        .slice(0, 300);
    }
    if (CEE_COUNTRY_CODES.has(iso2)) {
      return airportDatabase
        .filter((a) => a.country === iso2)
        .map(formatAirportForUi)
        .slice(0, 400);
    }
    if (iso2 === 'TR' || iso2 === 'KZ') {
      return airportDatabase
        .filter((a) => a.country === iso2)
        .map(formatAirportForUi)
        .slice(0, 400);
    }
    if (AFRICA_CODES.has(iso2)) {
      return airportDatabase
        .filter((a) => a.country === iso2)
        .map(formatAirportForUi)
        .slice(0, 400);
    }
    if (AMERICAS_LATAM_CODES.has(iso2)) {
      return airportDatabase
        .filter((a) => a.country === iso2)
        .map(formatAirportForUi)
        .slice(0, 800);
    }
    if (OCEANIA_EXTRA_CODES.has(iso2)) {
      return airportDatabase
        .filter((a) => a.country === iso2)
        .map(formatAirportForUi)
        .slice(0, 400);
    }
  }

  const ukMatches = [];
  const africaMatches = [];
  const europeMatches = [];
  const australiaMatches = [];
  const asiaMatches = [];
  const latamMatches = [];
  const oceaniaPacMatches = [];
  const usaMatches = [];
  const canadaMatches = [];
  const otherMatches = [];
  const seen = new Set();

  for (const airport of airportDatabase) {
    if (!airportMatchesQuery(airport, searchQuery)) continue;
    const id = airport.iataCode;
    if (seen.has(id)) continue;
    seen.add(id);
    if (UK_COUNTRY_CODES.has(airport.country)) {
      ukMatches.push(airport);
    } else if (AFRICA_CODES.has(airport.country)) {
      africaMatches.push(airport);
    } else if (EUROPE_CODES.has(airport.country)) {
      europeMatches.push(airport);
    } else if (AUSTRALIA_CODES.has(airport.country)) {
      australiaMatches.push(airport);
    } else if (ASIA_CODES.has(airport.country)) {
      asiaMatches.push(airport);
    } else if (AMERICAS_LATAM_CODES.has(airport.country)) {
      latamMatches.push(airport);
    } else if (OCEANIA_EXTRA_CODES.has(airport.country)) {
      oceaniaPacMatches.push(airport);
    } else if (USA_CODES.has(airport.country)) {
      usaMatches.push(airport);
    } else if (CANADA_CODES.has(airport.country)) {
      canadaMatches.push(airport);
    } else {
      otherMatches.push(airport);
    }
  }

  const combined = [
    ...ukMatches,
    ...africaMatches,
    ...europeMatches,
    ...australiaMatches,
    ...asiaMatches,
    ...latamMatches,
    ...oceaniaPacMatches,
    ...usaMatches,
    ...canadaMatches,
    ...otherMatches,
  ].slice(0, 65);

  return combined.map(formatAirportForUi);
};
